/**
 * useSignDocumentHandoff — Document Sign's intake for the cross-arche
 * "Sign this document" handoff (`sign_document`).
 *
 * Another arche hands over a Vault document and (optionally) a recipient
 * list; this turns that into a draft envelope and drops the user into the
 * builder to place fields and send. The handoff itself never sends anything.
 *
 * Three things are deliberate:
 *
 *   - **It lands on the BUILDER, never further downstream.** Prefilled
 *     recipients decide who receives a legally binding document, so the user
 *     has to pass through the screen where that list is visible and editable.
 *     Landing on review or send would let a handed-over recipient list reach
 *     a signature request without ever being looked at.
 *   - **The copy is server-idempotent, so a retry here is safe.** The route
 *     keys on `(holder, originArche, requestId)` and returns the same draft
 *     with `created: false`. That is what lets this attach the PDF as a
 *     separate step without risking a duplicate envelope if the attach fails
 *     and the user re-initiates.
 *   - **The origin comes from the host context, not the payload.** A producer
 *     naming its own origin would let one arche direct a return into another.
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useArcheHandoffIntake,
  type ArcheHandoffContext,
  type ArcheHandoffPayload,
} from '@/components/fias-ai';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { addBreadcrumb } from '@/lib/telemetry';
import { useSelectedHolder } from './useSelectedHolder';
import { useUploadDocument } from './useDocumentFileMutations';

const DOCUMENT_SIGN_ARCHE_ID = 'arche_document_sign';

function ensurePdfName(name: string): string {
  return /\.pdf$/i.test(name) ? name : `${name}.pdf`;
}

/**
 * Fetch the handed-over Vault document as a File, ready for the envelope's
 * normal upload pipeline.
 *
 * The bytes are COPIED rather than referenced, matching what the Fias file
 * picker already does: an envelope's source file is fixed at send and
 * hash-covered by the audit certificate, so it cannot share a row with a
 * document the user may later edit or delete.
 */
async function fetchHandoffDocument(documentId: string): Promise<File> {
  const { url, document } = await api.getVaultDocumentDownloadUrl(documentId);
  const response = await fetch(url);
  if (!response.ok) throw new Error('The document could not be downloaded.');
  const blob = await response.blob();
  return new File([blob], ensurePdfName(document.name), {
    type: 'application/pdf',
  });
}

export function useSignDocumentHandoff(): { isImporting: boolean } {
  const navigate = useNavigate();
  const { holderId } = useSelectedHolder();
  const uploadDocument = useUploadDocument();
  const [isImporting, setIsImporting] = useState(false);

  const apply = useCallback(
    async (payload: ArcheHandoffPayload, context: ArcheHandoffContext) => {
      if (payload.kind !== 'sign_document') return;

      // The origin is what makes a return possible. Without it there is
      // nowhere to send the user back to, so refuse rather than silently
      // producing a one-way envelope the calling arche can never reconcile.
      if (!context.originArcheId) {
        addBreadcrumb('document-sign', 'Dropped sign_document handoff with no origin');
        toast({
          title: 'Could not open this document',
          description: 'The request did not say which app it came from.',
          variant: 'destructive',
        });
        return;
      }
      if (!holderId) {
        toast({
          title: 'Could not open this document',
          description: 'No workspace is selected.',
          variant: 'destructive',
        });
        return;
      }

      setIsImporting(true);
      try {
        const { envelope, needsSourceFile } = await api.createDocumentSignEnvelopeFromHandoff({
          holderId,
          requestId: payload.requestId,
          originArcheId: context.originArcheId,
          documentId: payload.documentId,
          title: payload.title,
          message: payload.message,
          signingOrder: payload.signingOrder,
          recipients: payload.recipients,
          correlationId: payload.correlationId,
        });

        // Attach the PDF only when the envelope does not already have one.
        //
        // Keyed on the FILES, not on whether the draft was just created: the
        // copy happens after the draft exists, so an upload that fails leaves
        // a real envelope with nothing in it. Keying on `created` would make
        // the retry skip the attach and strand the user on a permanently
        // empty draft. Asking what the envelope actually has makes the whole
        // intake resumable, and still never double-attaches.
        if (needsSourceFile) {
          const file = await fetchHandoffDocument(payload.documentId);
          await uploadDocument.mutateAsync({ envelopeId: envelope.envelopeId, file });
        }

        navigate(`/a/${DOCUMENT_SIGN_ARCHE_ID}/envelope/${envelope.envelopeId}`, {
          replace: true,
        });
      } catch (err) {
        toast({
          title: 'Could not open this document',
          description: err instanceof Error ? err.message : 'An error occurred.',
          variant: 'destructive',
        });
      } finally {
        setIsImporting(false);
      }
    },
    [holderId, navigate, uploadDocument],
  );

  useArcheHandoffIntake(DOCUMENT_SIGN_ARCHE_ID, (payload, context) => {
    void apply(payload, context);
  });

  return { isImporting };
}
